import React from 'react';
import '../../shared/style.css';
import { Button, Group, Dialog, DialogContentDrawerRenderProps } from '@economic/taco';
import { Field } from '../../../../../shared/components/Form';
import { t } from '../../../../../shared/components/l10n/l10n';
import { DisplayMessages } from '../../shared/components/DisplayMessages';
import { InvoicingMessage, InvoicingMessageType, SendByType } from '../../invoices/book-and-send/types';
import { useEmailOptionsPdfSelected, useEmailOptionsRecipientsSelected } from '../../invoices/utils';
import { SendAsEmail } from '../../shared/components/SendBy';
import { EmailOptionsDrawerToggle } from '../../shared/components/EmailOptionsDrawerToggle';
import { SalesDialogFormWithEmailOptionsDrawer } from '../../shared/components/SalesDialogFormWithEmailOptionsDrawer';
import { EmailTemplatesFormState } from '../../invoices/utils/emailTemplateFormState';
import { emailTemplateUpdater } from '../../invoices/utils/emailTemplateUpdater';
import { EmailTemplatePicker } from '../../invoices/utils/EmailTemplatePicker';
import { EmailOrEInvoiceSendingLimitExceededAlert } from '../../../shared/EmailOrEInvoiceSendingLimitExceededAlert';

export type BookAndSendReminderFormData = {
    emailOptions: {
        showPdf: boolean;
        recipients: string;
        recipientsCc: string;
        recipientsBcc: string;
        subject: string;
        saveSubject: boolean;
        body: string;
        saveBody: boolean;
        includePdfLink: boolean;
        encrypt: boolean;
        includePdfAsAttachment: boolean;
    };
    exceededSendingLimit: boolean;
} & EmailTemplatesFormState;

function useRequiredFieldsValidation(): InvoicingMessage[] {
    const requiredFieldsMessages = [] as InvoicingMessage[];
    // check if email options make sense for sending order.
    const isPdfSelected = useEmailOptionsPdfSelected();
    // check if at least one recipient fields is entered
    const isRecipientSelected = useEmailOptionsRecipientsSelected();

    if (!isPdfSelected) {
        requiredFieldsMessages.push({ message: t`FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR`, type: InvoicingMessageType.Error });
    }

    if (!isRecipientSelected) {
        requiredFieldsMessages.push({
            message: t`FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR`,
            type: InvoicingMessageType.Error,
        });
    }

    return requiredFieldsMessages;
}

interface BookAndSendReminderDialogProps {
    information?: BookAndSendReminderFormData;
    loading: boolean;
    onBookAndSend: (values: BookAndSendReminderFormData) => Promise<any>;
    onHide: () => void;
    open: boolean;
}

export function BookAndSendSingleReminderDialog(props: BookAndSendReminderDialogProps) {
    const { information, loading, onBookAndSend, onHide, open } = props;

    function bookAndSend(form: any) {
        form.change('sendByType', Number(SendByType.email));
        form.blur('sendByType');
        form.submit();
    }

    function updater() {
        return [emailTemplateUpdater()];
    }

    return (
        <SalesDialogFormWithEmailOptionsDrawer
            loading={loading}
            ariaLabel={t(`FORM_BOOKANDSEND_REMINDER_HEADER`)}
            open={open}
            onHide={onHide}
            className="book-and-send-dialog form h-[500px]"
            size="xs"
            initialData={information}
            onSubmit={onBookAndSend}
            updates={updater}>
            {(form, drawer) =>
                information && (
                    <BookAndSendReminderDialogContent
                        information={information}
                        drawer={drawer}
                        onBookAndSend={() => bookAndSend(form)}
                    />
                )
            }
        </SalesDialogFormWithEmailOptionsDrawer>
    );
}

interface BookAndSendReminderDialogContentProps {
    information: BookAndSendReminderFormData;
    drawer: DialogContentDrawerRenderProps;
    onBookAndSend: () => void;
}

function BookAndSendReminderDialogContent(props: BookAndSendReminderDialogContentProps) {
    const { information, drawer, onBookAndSend } = props;
    const requiredFieldValidationMessages = useRequiredFieldsValidation();

    return (
        <div className="h-full flex flex-col">
            <Dialog.Title>{t(`FORM_BOOKANDSEND_REMINDER_HEADER`)}</Dialog.Title>

            <SendAsEmail title={t`FORM_BOOKANDSEND_REMINDER_TEXT`} />

            <EmailOptionsDrawerToggle drawer={drawer} className="self-start" />

            <EmailTemplatePicker templates={information.templates} />

            <div className="overflow-auto grow">
                {information.exceededSendingLimit && <EmailOrEInvoiceSendingLimitExceededAlert />}
                <DisplayMessages messages={requiredFieldValidationMessages} />
            </div>

            <div className="book-and-send-dialog__options">
                <Field.Checkbox name="emailOptions.showPdf" text={t`FORM_BOOKANDSEND_SHOW_REMINDER`} />
            </div>

            <Field.Hidden name="sendByType" value={Number(SendByType.undefined)} />

            <Dialog.Footer>
                <Group>
                    <Button
                        appearance="primary"
                        type="submit"
                        disabled={information.exceededSendingLimit || !!requiredFieldValidationMessages.length}
                        onClick={onBookAndSend}>
                        {t`FORM_BOOKANDSEND_REMINDER_BOOKANDSEND`}
                    </Button>
                </Group>
            </Dialog.Footer>
        </div>
    );
}
